import React from 'react'
import OrdersTable from 'components/OrdersTable'
import useFilters from '@byma/shared/hooks/useFilters'
import dayjs from 'dayjs'
import Link from 'next/link'
import Image from 'next/image'
import Router, { useRouter } from 'next/router'
import useToastContext from "@byma/shared/hooks/useToastContext";
import Layout from 'components/Layout'
import { methodParser, money } from '@byma/shared/helpers/formatters'
import useAuth from '@byma/shared/hooks/useAuth'
import { ImWhatsapp } from 'react-icons/im'
import useSWR from 'swr'
import fetcher from '@byma/shared/helpers/fetcher'
import jwt from 'jsonwebtoken'
import Loader from '@byma/shared/components/Loader'

const refundStatusParser = {
    refunded: 'Processado',
    failed: 'Falhou',
    pending: 'Pendente',
}

export const statusParser = {
    paid: 'Pago',
    pending: 'Pendente',
    canceled: 'Cancelado',
    // Custom parser so rename is possible
    refunded: 'Cancelado',
    expired: 'Expirado',
    refused: 'Não autorizado',
    errored: 'Erro',
    'order placed': 'Pendente',
    POS: 'Pago'
}

export default function Orders() {
    const session = useAuth()
    let { query, ...router } = useRouter()
    const { orderToken } = query
    const { token, user: currentUser } = session
    const { originalQueryString } = useFilters(query)

    const { data: orderSelected } = useSWR(token && orderToken ? ['/api/orders/get?orderToken=' + orderToken, null, token] : null, fetcher)
    const [isRefunding, setIsRefunding] = React.useState(false)
    const addToast = useToastContext()

    const inputRef = React.useRef(null)

    function copyURL(e) {
        if (inputRef.current) {
            inputRef.current.select()
            document.execCommand('copy')
            e.target.focus()
            addToast('Copiado!')
        }
    }

    const orderLink = (order) => `https://byma.com.br/order/${order.token}`
    const shareString = (order) => `Associe seus ingressos a sua conta Byma em ${orderLink(order)}`

    async function refundOrder(order) {
        if (window.prompt(`Digite cancelar para confirmar cancelamento e estorno (se aplicável) da fatura ${order.token}`) !== 'cancelar') {
            return false
        }
        setIsRefunding(true)
        try {
            const res = await fetcher(`/api/orders/refund?orderToken=${order.token}`, { method: 'PUT' }, token)
            addToast('Venda estornada com sucesso')
            router.push(`/orders?${originalQueryString}`)
            //refreshOrders()
        } catch (e) {
            addToast('Não foi possível estornar esta venda', 'error')
            console.log(e)
        }
        setIsRefunding(false)
    }

    return (
        <Layout {...{ session }}>
            <div className="flex divide-x-0 lg:divide-x gap-3 h-full">
                <div className="w-full">
                    <OrdersTable />
                </div>
                <div className={`fixed w-full h-full inset-0 transition-opacity duration-100 ${orderToken ? 'z-50 opacity-100' : '-z-1 opacity-0'}`} onClick={() => router.push(`/orders?${originalQueryString}`)}>
                    <div class="absolute w-full h-full bg-gray-900 opacity-50" />
                    <div class="modal-content p-3 absolute bg-white w-full md:w-11/12 md:max-w-2xl md:mx-auto md:rounded md:shadow-lg z-50 overflow-y-auto max-h-screen" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end">
                            <Link href={`/orders?${originalQueryString}`}>
                                <a>
                                    <button>
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </a>
                            </Link>
                        </div>
                        {orderSelected ? (
                            <>
                                <div className="mb-6">
                                    <div className="mb-3">
                                        {orderSelected?.result?.partyId?.pictures?.photos[0] ? (
                                            <img src={orderSelected?.result?.partyId?.pictures.photos[0]} alt={orderSelected?.result?.partyId?.name} className="rounded h-32 object-cover w-full" />
                                        ) : (
                                            <div className="h-32 bg-gray-400 rounded overflow-hidden" />
                                        )}
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <p className="text-xl font-bold">Venda #{orderSelected.result.token}</p>
                                        <p className="text-gray-700">{dayjs(orderSelected.result.created_at).format('DD/MM/YYYY HH:mm:ss')}</p>
                                    </div>
                                    <div className="box w-full mt-6">
                                        <div className="flex justify-between items-center">
                                            <div className="min-w-0 text-left">
                                                <p className="text-md font-bold">Evento</p>
                                                <p className="truncate">
                                                    {orderSelected?.result?.partyId?.name || 'N/A'}
                                                </p>
                                                <p className="truncate">
                                                    {orderSelected?.result?.partyId?._id || 'N/A'}
                                                </p>
                                            </div>
                                            <div className="min-w-0 text-right">
                                                <p className="text-md">{dayjs(orderSelected?.result?.partyId?.date).format('DD/MM/YYYY HH:mm:ss')}</p>
                                                <p className="text-xs">
                                                    {orderSelected?.result?.partyId?.location?.name|| 'N/A'} - {orderSelected?.result?.partyId?.location?.address || 'N/A'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    { orderSelected?.result?.created_by && (
                                        <div className="box w-full mt-6">
                                            <div className="min-w-0 text-left">
                                                <p className="text-md font-bold">Criada por</p>
                                                <p className="truncate">
                                                    {orderSelected?.result?.created_by?.profile?.name || 'Usuário não associado'}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    <div className="box w-full mt-6">
                                        <p className="text-md font-bold">Pagamento</p>
                                        <div className="flex justify-between items-center box">
                                            <div className="text-left min-w-0">
                                                <p className="truncate">{orderSelected.result?.payment?.method && methodParser[orderSelected.result.payment.method]}</p>
                                                <p className="truncate text-sm text-gray-700">{orderSelected.result?.payment?.meta_data?.external_id}</p>
                                                <p className="truncate text-sm text-gray-700">
                                                    {orderSelected.result?.payment?.method === 'online_credit' && orderSelected.result?.payment?.meta_data?.soft_descriptor_code}
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <p className={`label text-center ${orderSelected.result.status === 'paid' || orderSelected.result.status === 'POS' ? 'success' : 'info'}`}>{statusParser[orderSelected.result.status]}</p>
                                                <p className="truncate text-sm text-gray-700">
                                                    {orderSelected.result?.payment?.meta_data?.cardId?.brand}
                                                </p>
                                                <p className="truncate text-sm text-gray-700">
                                                    {orderSelected.result?.payment?.meta_data?.cardId?.first_digits}
                                                    {orderSelected.result?.payment?.meta_data?.cardId?.first_digits && '******'}
                                                    {orderSelected.result?.payment?.meta_data?.cardId?.last_digits}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="box">
                                            <div className="flex justify-between items-center border-b">
                                                <p>Total pago</p>
                                                <p className="text-md">{money(orderSelected.result.payment?.total_amount_cents / 100)}</p>
                                            </div>
                                            <div className="flex justify-between items-center border-b">
                                                <p>Total dos ingressos</p>
                                                <p className="text-md">{money(orderSelected.result.payment?.order_amount_cents / 100)}</p>
                                            </div>
                                            <div className="flex justify-between items-center border-b">
                                                <p>Taxas consumidor</p>
                                                <p className="text-md">{money(orderSelected.result.payment?.customer_amount_cents / 100)}</p>
                                            </div>
                                            <div className="flex justify-between items-center border-b">
                                                <p>Taxas produtor</p>
                                                <p className="text-md">{money(orderSelected.result.payment?.producer_amount_cents / 100)}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="box w-full mt-6">
                                        <div className="flex justify-between items-center">
                                            <div className="min-w-0 text-left">
                                                <p className="text-md font-bold">Usuário associado</p>
                                                <p className="truncate">
                                                    {orderSelected?.result?.userId?.profile?.name || 'Usuário não associado'}
                                                </p>
                                            </div>
                                            <div className="min-w-0 text-right">
                                                <p className="truncate">
                                                    {orderSelected?.result?.userId?.email}
                                                </p>
                                                <p className="truncate">
                                                    {orderSelected?.result?.userId?.profile.cpf}
                                                </p>
                                            </div>
                                        </div>
                                        {(!orderSelected.result.userId && (!orderSelected?.result?.meta?.printTickets && !orderSelected?.result?.meta?.physicalTickets)) && (
                                            <div className="grid gap-3 mt-3">
                                                <input className="w-full p-3 shadow rounded" value={orderLink(orderSelected.result)} ref={inputRef} />
                                                <button className="flex gap-1 items-center btn btn-primary w-full" type="button" onClick={copyURL}>
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                        <path d="M7 9a2 2 0 012-2h6a2 2 0 012 2v6a2 2 0 01-2 2H9a2 2 0 01-2-2V9z" />
                                                        <path d="M5 3a2 2 0 00-2 2v6a2 2 0 002 2V5h8a2 2 0 00-2-2H5z" />
                                                    </svg>
                                                    Copiar
                                                </button>
                                                <a href={`https://wa.me/?text=${shareString(orderSelected.result)}`} target="_blank" className="block">
                                                    <button type="button" className="flex gap-1 items-center btn btn-whatsapp w-full">
                                                        <ImWhatsapp />
                                                        Enviar pelo Whatsapp
                                                    </button>
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                    <div className="box w-full mt-6">
                                        <div className="text-left">
                                            <p className="text-md font-bold">Ingressos</p>
                                        </div>
                                        <div className="mt-3">
                                            {orderSelected?.result?.tickets?.map(ticket => (
                                                <div className="box mb-3 flex justify-between items-center">
                                                    <div className="min-w-0">
                                                        <p className="truncate">{ticket.ticketType.name}</p>
                                                        <p className="text-sm text-gray-700 truncate">{ticket.guestInfo.name}</p>
                                                    </div>
                                                    <div>
                                                        <p>{money(ticket.ticketType.cost)}</p>
                                                        <p className={`label text-center ${ticket?.status === 'paid' || ticket.status === 'POS' ? 'success' : 'info'}`}>{statusParser[ticket?.status]}</p>
                                                        <Link href={`/tickets/${ticket._id}`}>
                                                            <a className="text-blue-700 hover:underline">
                                                                Ir para ingresso 
                                                            </a>
                                                        </Link>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                            <div className="box w-full mt-6">
                                <div className="text-left">
                                    <p className="text-md font-bold">Estornos</p>
                                </div>
                                <div className="mt-3">
                                    {orderSelected?.result?.refunds?.length === 0 && (
                                        <p className="text-gray-700 text-sm">Nenhum estorno realizado</p>
                                    )}
                                    {orderSelected?.result?.refunds?.map(refund=> (
                                        <div className="box mb-3 flex justify-between items-center">
                                            <div>
                                                <p className="truncate">{dayjs(refund.created_at).format('DD/MM/YYYY HH:mm:ss')}</p>
                                                <p className="text-md">{refund.ticketsRefunded.length} ingresso{refund.ticketsRefunded.length > 1 ? 's' : ''}</p>
                                                <p className="text-sm">Estornado por {refund.created_by?.profile?.name}</p>
                                            </div>
                                            <div className="min-w-0 text-right">
                                                <p className={`label text-center ${refund.status === 'pending' && 'info'} ${refund.status === 'refunded' && 'success'} ${ refund.status === 'failed' && 'bg-red-500'}`}>
                                                {refundStatusParser[refund?.status]}
                                                </p>
                                                <p className="text-md">Total antigo: {money(refund.previousTotal)}</p>
                                                <p className="text-md">Novo total: {money(refund.newTotal)}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                                    {dayjs(orderSelected?.result?.partyId?.date).isAfter(dayjs()) && (
                                        <>
                                            { isRefunding ? (
                                                <div className="w-full md:w-64">
                                                    <Loader />
                                                </div>
                                            ) : (
                                                <button className="btn btn-blue" onClick={() => refundOrder(orderSelected.result)}>Cancelar venda</button>
                                            )}
                                        </>
                                    )}
                                </div>
                            </>
                        ) : (
                            <div className="w-full py-12">
                                <Loader /> 
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    )
}
